/**
 * Shapes returned by SnappyMail's JSON API.
 *
 * Transcribed from the `jsonSerialize()` methods in the vendored source rather
 * than guessed:
 *   MailSo/Imap/Folder.php, MailSo/Mail/MessageCollection.php,
 *   MailSo/Mail/Message.php, MailSo/Mime/Email.php, MailSo/Imap/BodyStructure.php
 *
 * Only the fields this app actually uses are declared. Everything else the API
 * sends is ignored on purpose -- listing it would imply we depend on it, and the
 * fewer fields we name, the less an upstream change can break.
 */

export interface EmailAddress {
	name: string;
	email: string;
}

/** IMAP special-use role, when the server advertises one. */
export type FolderRole = 'inbox' | 'sent' | 'drafts' | 'trash' | 'junk' | 'archive' | null;

export interface Folder {
	name: string;
	fullName: string;
	delimiter: string;
	attributes: string[];
	totalEmails: number;
	unreadEmails: number;
	role: FolderRole;
}

export interface FolderCollection {
	'@Collection': Folder[];
	quotaUsage: number | null;
	quotaLimit: number | null;
	capabilities: string[];
}

export interface Attachment {
	folder: string;
	uid: number;
	mimeIndex: string;
	mimeType: string;
	fileName: string;
	estimatedSize: number;
	cId: string;
	isInline: boolean;
}

/** A row in the message list. The list omits bodies; only `preview` is present. */
export interface MessageSummary {
	folder: string;
	uid: number;
	subject: string;
	dateTimestamp: number;
	from: EmailAddress[];
	to: EmailAddress[];
	flags: string[];
	preview?: string;
	attachments?: Attachment[] | { '@Collection': Attachment[] };
	threads?: number[];
	size: number;
}

/** A fully fetched message. `html` and `plain` only exist once opened. */
export interface Message extends MessageSummary {
	html?: string;
	plain?: string;
	messageId?: string;
	inReplyTo?: string;
	references?: string;
	cc?: EmailAddress[];
	replyTo?: EmailAddress[];
}

export interface MessageList {
	'@Collection': MessageSummary[];
	totalEmails: number;
	offset: number;
	limit: number;
	search: string;
	folder: {
		name: string;
		totalEmails: number;
		unreadEmails: number;
		uidNext: number;
	};
}

/**
 * IMAP flags are matched case-insensitively.
 *
 * The API returns them as sent by the server, and servers differ on case
 * ("\Seen" vs "\seen"). Comparing raw strings works until it silently does not,
 * which for the unread state means every message looking unread.
 */
export function hasFlag(flags: string[] | undefined, flag: string): boolean {
	const wanted = flag.toLowerCase();
	return (flags ?? []).some(item => item.toLowerCase() === wanted);
}

export const isUnread = (message: { flags?: string[] }): boolean =>
	!hasFlag(message.flags, '\\Seen');

export const isFlagged = (message: { flags?: string[] }): boolean =>
	hasFlag(message.flags, '\\Flagged');

/** Attachments arrive either bare or wrapped in a collection envelope. */
export function attachmentsOf(message: MessageSummary | Message): Attachment[] {
	const raw = message.attachments;
	if (!raw) {
		return [];
	}
	const list = Array.isArray(raw) ? raw : raw['@Collection'];
	// Inline images belong to the body, not the attachment strip -- showing them
	// there means every signature logo appears as a file to download.
	return (list ?? []).filter(item => !item.isInline);
}

export function displayName(addresses: EmailAddress[] | undefined): string {
	const first = addresses?.[0];
	if (!first) {
		return 'Unknown';
	}
	return first.name || first.email || 'Unknown';
}

export function addressLine(addresses: EmailAddress[] | undefined): string {
	return (addresses ?? [])
		.map(item => (item.name ? `${item.name} <${item.email}>` : item.email))
		.join(', ');
}
